import { createFileRoute, Link } from "@tanstack/react-router";
import { absoluteUrl } from "@/lib/site";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { listShopProducts } from "@/lib/shop.functions";
import { BOOKS } from "@/lib/content";
import { useOwnership } from "@/hooks/use-ownership";

const productsQuery = queryOptions({
  queryKey: ["shop", "products"],
  queryFn: () => listShopProducts(),
});

export const Route = createFileRoute("/shop/")({
  head: () => ({
    meta: [
      { title: "Shop · Buddha KA · Artifacts" },
      { name: "description", content: "Objects, garments, and artifacts carrying instruction from the KAfrequency world." },
      { property: "og:title", content: "Shop · Buddha KA" },
      { property: "og:description", content: "Objects, garments, and artifacts from the KAfrequency world." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/shop" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/shop") }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQuery),
  component: ShopIndex,
});

function formatPrice(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
}

function ShopIndex() {
  const { data } = useQuery(productsQuery);
  const products = data ?? [];
  const ownership = useOwnership();

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <header className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Artifacts</p>
        <h1 className="mt-4 font-serif text-5xl">The Shop.</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Objects, garments, and transmissions carrying instruction from the KAfrequency world.
        </p>
      </header>

      {/* Featured Books — external products (e.g. Apple Books) */}
      {(() => {
        const featuredBooks = BOOKS.filter((b) => b.featured && b.externalPurchaseUrl);
        if (featuredBooks.length === 0) return null;
        return (
          <section className="mt-14" aria-label="Featured books">
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Featured Books</p>
                <h2 className="mt-2 font-serif text-2xl">Read the transmission.</h2>
              </div>
              <Link to="/books" className="text-xs text-gold hover:text-gold-luminous">All books →</Link>
            </div>
            <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {featuredBooks.map((b) => (
                <li key={b.slug} className="group overflow-hidden rounded-xl border hairline bg-charcoal">
                  <Link to="/books/$slug" params={{ slug: b.slug }} className="block">
                    <div className="aspect-[4/5] w-full overflow-hidden bg-obsidian">
                      {b.coverUrl ? (
                        <img src={b.coverUrl} alt={b.coverAlt ?? `${b.title} — cover`} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" loading="lazy" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-muted-foreground">Book</div>
                      )}
                    </div>
                    <div className="p-5">
                      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Book · External</p>
                      <h3 className="mt-2 font-serif text-xl group-hover:text-gold-luminous">{b.title}</h3>
                      {b.author && (
                        <p className="mt-1 text-xs text-muted-foreground">by {b.author}{b.authorLegalName ? ` (${b.authorLegalName})` : ""}</p>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })()}

      {products.length === 0 ? (
        <p className="mt-16 rounded-xl border hairline bg-charcoal p-10 text-center text-sm text-muted-foreground">
          The first artifacts are being prepared. Check back soon.
        </p>
      ) : (
        <ul className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <li key={p.slug} className="group overflow-hidden rounded-xl border hairline bg-charcoal">
              <Link to="/shop/$slug" params={{ slug: p.slug }} className="block">
                <div className="aspect-[4/5] w-full overflow-hidden bg-obsidian">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" loading="lazy" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-muted-foreground">
                      {p.kind}
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{p.kind}</p>
                  <h2 className="mt-2 font-serif text-xl group-hover:text-gold-luminous">{p.title}</h2>
                  {p.subtitle && <p className="mt-1 text-xs text-muted-foreground">{p.subtitle}</p>}
                  <p className="mt-4 font-serif text-lg text-gold-luminous">
                    {ownership.owns(p.slug) ? (
                      <span className="text-sm uppercase tracking-[0.25em] text-gold">In your library</span>
                    ) : p.external_purchase_url ? (
                      "External"
                    ) : (
                      formatPrice(p.amount_cents, p.currency)
                    )}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
